/*
 * Purpose: Pre-built workflows for everyday (non-technical) users
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { Workflow } from '@/stores/workflowStore';

type UserTemplate = Workflow & {
  difficulty: string;
  category: string;
  features: string[];
  useCase: string;
};

const now = () => new Date().toISOString();

export const AVERAGE_USER_TEMPLATES: Record<string, UserTemplate> = {
  linkedin_job_hunt_pipeline: {
    id: 'template-005',
    name: '💼 LinkedIn Job Hunt (end-to-end)',
    difficulty: 'Intermediate',
    category: 'Career & Jobs',
    useCase:
      'When LinkedIn Jobs emails you about new roles, score fit, tailor your resume, write a cover letter, apply, track everything, schedule follow-up, and message the hiring contact.',
    description:
      'Full job-search automation: email alert → relevance check → resume + cover letter → apply on LinkedIn or company site → tracker dashboard → follow-up reminder → LinkedIn intro message.',
    features: [
      'Email / webhook trigger',
      'Relevance scoring',
      'Resume pool + fact-only tailoring',
      'Cover letter to hiring manager',
      'LinkedIn + website apply',
      'Application tracker',
      'Follow-up scheduler',
      'LinkedIn outreach',
    ],
    nodes: [
      {
        id: 'li-email-trigger',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'LinkedIn Jobs email received',
          url: 'https://your-automation.com/webhook/email/linkedin-jobs',
          description:
            'Gmail/Outlook rule: when email from linkedin.com subject contains "job alert", forward parsed JSON here.',
        },
        x: 40,
        y: 120,
      },
      {
        id: 'li-parse-roles',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '1) Parse job listings from email',
          transform: `const roles = input.roles || [];
return {
  source: 'linkedin_jobs_email',
  receivedAt: new Date().toISOString(),
  roles: roles.map((r, i) => ({
    id: 'role-' + i,
    title: r.title,
    company: r.company,
    url: r.url,
    location: r.location || '',
    hiringManager: r.hiringManager || null,
    applyUrl: r.applyUrl || r.url,
  })),
  rawSubject: input.subject,
};`,
        },
        x: 280,
        y: 120,
      },
      {
        id: 'li-score-relevance',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '2) Score relevance vs your profile',
          transform: `const profile = {
  targetTitles: ['Product Manager', 'Program Manager', 'Technical PM'],
  mustHaveSkills: ['roadmap', 'stakeholders', 'agile'],
  locations: ['Remote', 'San Francisco', 'Hybrid'],
  minRelevance: 0.65,
};
const score = (role) => {
  let s = 0;
  const t = (role.title || '').toLowerCase();
  if (profile.targetTitles.some((x) => t.includes(x.toLowerCase()))) s += 0.4;
  const blob = JSON.stringify(role).toLowerCase();
  s += profile.mustHaveSkills.filter((k) => blob.includes(k)).length * 0.1;
  if (profile.locations.some((l) => (role.location || '').includes(l))) s += 0.2;
  return Math.min(1, s);
};
const roles = (input.roles || []).map((r) => ({ ...r, relevance: score(r) }));
const relevant = roles.filter((r) => r.relevance >= profile.minRelevance);
return {
  ...input,
  roles,
  relevantRoles: relevant,
  irrelevantCount: roles.length - relevant.length,
  hasRelevant: relevant.length > 0,
};`,
        },
        x: 520,
        y: 120,
      },
      {
        id: 'li-check-relevant',
        type: 'tool',
        subtype: 'conditional',
        config: {
          name: 'Any roles worth pursuing?',
          condition: 'input.hasRelevant === true',
        },
        x: 760,
        y: 120,
      },
      {
        id: 'li-skip-log',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Skip — no relevant roles',
          message: 'No roles met relevance threshold; nothing to apply for.',
        },
        x: 760,
        y: 280,
      },
      {
        id: 'li-pick-resume',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '3) Pick best base resume from pool',
          transform: `const pool = input.resumePool || [
  { id: 'pm-general', label: 'PM — general', tags: ['product', 'roadmap'] },
  { id: 'pm-saas', label: 'PM — SaaS', tags: ['b2b', 'saas'] },
  { id: 'pm-technical', label: 'PM — technical', tags: ['api', 'engineering'] },
];
const role = (input.relevantRoles || [])[0];
const pick = pool.find((r) =>
  r.tags.some((t) => JSON.stringify(role).toLowerCase().includes(t))
) || pool[0];
return { ...input, role, selectedResume: pick };`,
        },
        x: 1000,
        y: 40,
      },
      {
        id: 'li-tailor-resume',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '4) Tailor resume (facts only)',
          transform: `const role = input.role;
return {
  ...input,
  tailoredResume: {
    baseId: input.selectedResume?.id,
    highlights: [
      'Lead with experience matching: ' + role.title,
      'Mirror keywords from posting in bullet order',
      'Keep dates, companies, titles unchanged',
    ],
    outputPath: 'applications/' + role.company + '-' + Date.now() + '-resume.pdf',
    factCheckRequired: true,
  },
};`,
        },
        x: 1240,
        y: 40,
      },
      {
        id: 'li-cover-letter',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '5) Cover letter to hiring manager',
          transform: `const role = input.role;
const manager = role.hiringManager || { name: 'Hiring Manager', title: 'Recruiting Team' };
return {
  ...input,
  coverLetter: {
    to: manager.name,
    title: manager.title || 'Hiring Manager',
    company: role.company,
    role: role.title,
    bodyOutline: [
      'Opening: role + why this company',
      '2–3 fact-based wins from tailored resume',
      'Close: enthusiasm + availability for conversation',
    ],
    outputPath: 'applications/' + role.company + '-cover-letter.pdf',
  },
};`,
        },
        x: 1480,
        y: 40,
      },
      {
        id: 'li-apply-linkedin',
        type: 'tool',
        subtype: 'http',
        config: {
          name: '6a) Apply via LinkedIn Easy Apply',
          method: 'POST',
          url: 'https://api.linkedin.com/v2/jobApplications',
          headers: '{"Authorization": "Bearer $LINKEDIN_TOKEN", "Content-Type": "application/json"}',
          description: 'Submit tailored resume + cover letter through LinkedIn Apply Now.',
        },
        x: 1720,
        y: 40,
      },
      {
        id: 'li-check-external',
        type: 'tool',
        subtype: 'conditional',
        config: {
          name: 'Redirected to company careers site?',
          condition:
            'input.role && input.role.applyUrl && !String(input.role.applyUrl).includes("linkedin.com")',
        },
        x: 1960,
        y: 40,
      },
      {
        id: 'li-apply-website',
        type: 'tool',
        subtype: 'http',
        config: {
          name: '6b) Apply on company website',
          method: 'POST',
          url: 'https://your-automation.com/apply/browser-session',
          description: 'Fills ATS form using the same fact-checked resume package.',
        },
        x: 1960,
        y: 200,
      },
      {
        id: 'li-log-tracker',
        type: 'action',
        subtype: 'storage',
        config: {
          name: '7) Log to application tracker',
          key: 'job_application_tracker',
          description: 'Dashboard: role, company, score, channel, timestamps, status.',
        },
        x: 2200,
        y: 120,
      },
      {
        id: 'li-schedule-followup',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '8) Schedule follow-up reminder',
          transform: `const days = input.followUpDays || 5;
const due = new Date();
due.setDate(due.getDate() + days);
return {
  ...input,
  followUp: {
    dueAt: due.toISOString(),
    channel: 'email+calendar',
    note: 'Follow up on ' + input.role?.title + ' at ' + input.role?.company,
    calendarEventTitle: 'Follow up: ' + input.role?.company,
  },
};`,
        },
        x: 2440,
        y: 120,
      },
      {
        id: 'li-followup-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Reminder: follow up on application',
          type: 'info',
          message: 'Follow-up scheduled — you will be reminded to check application status.',
        },
        x: 2680,
        y: 120,
      },
      {
        id: 'li-linkedin-message',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: '9) Draft LinkedIn intro to hiring contact',
          transform: `const role = input.role;
const contact = role.hiringManager || { name: 'there', linkedInUrl: '' };
return {
  ...input,
  linkedInOutreach: {
    to: contact.name,
    profileUrl: contact.linkedInUrl,
    message: 'Hi ' + contact.name + ' — I recently applied for the ' + role.title +
      ' role at ' + role.company + ' and am very interested. Would you be open to sharing ' +
      'a bit more about the team and what success looks like in the first 90 days?',
    sendAfter: 'optional — review before sending',
  },
};`,
        },
        x: 2920,
        y: 120,
      },
      {
        id: 'li-send-linkedin',
        type: 'action',
        subtype: 'notification',
        config: {
          name: '10) Queue LinkedIn message (review first)',
          type: 'info',
          message: 'LinkedIn intro drafted — approve in dashboard before sending.',
        },
        x: 3160,
        y: 120,
      },
      {
        id: 'li-complete-log',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Pipeline complete',
          message: 'Job hunt workflow finished for this role.',
        },
        x: 3400,
        y: 120,
      },
    ],
    edges: [
      { id: 'li-e1', from: 'li-email-trigger', to: 'li-parse-roles' },
      { id: 'li-e2', from: 'li-parse-roles', to: 'li-score-relevance' },
      { id: 'li-e3', from: 'li-score-relevance', to: 'li-check-relevant' },
      { id: 'li-e4', from: 'li-check-relevant', to: 'li-pick-resume' },
      { id: 'li-e5', from: 'li-check-relevant', to: 'li-skip-log' },
      { id: 'li-e6', from: 'li-pick-resume', to: 'li-tailor-resume' },
      { id: 'li-e7', from: 'li-tailor-resume', to: 'li-cover-letter' },
      { id: 'li-e8', from: 'li-cover-letter', to: 'li-apply-linkedin' },
      { id: 'li-e9', from: 'li-apply-linkedin', to: 'li-check-external' },
      { id: 'li-e10', from: 'li-check-external', to: 'li-apply-website' },
      { id: 'li-e11', from: 'li-check-external', to: 'li-log-tracker' },
      { id: 'li-e12', from: 'li-apply-website', to: 'li-log-tracker' },
      { id: 'li-e13', from: 'li-log-tracker', to: 'li-schedule-followup' },
      { id: 'li-e14', from: 'li-schedule-followup', to: 'li-followup-notify' },
      { id: 'li-e15', from: 'li-followup-notify', to: 'li-linkedin-message' },
      { id: 'li-e16', from: 'li-linkedin-message', to: 'li-send-linkedin' },
      { id: 'li-e17', from: 'li-send-linkedin', to: 'li-complete-log' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },

  linkedin_jobs_relevance_only: {
    id: 'template-006',
    name: '📬 LinkedIn Jobs — relevance filter only',
    difficulty: 'Beginner',
    category: 'Career & Jobs',
    useCase:
      'Start small: when LinkedIn emails you about jobs, show only roles that match your goals.',
    description:
      'Parse LinkedIn job alert emails and surface only roles that match your title, skills, and location.',
    features: ['Email trigger', 'Job parsing', 'Relevance score', 'Daily digest notification'],
    nodes: [
      {
        id: 'lj-trigger',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'LinkedIn job alert email',
          url: 'https://your-automation.com/webhook/email/linkedin',
        },
        x: 80,
        y: 160,
      },
      {
        id: 'lj-parse',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Extract roles from email',
          transform: `return { roles: input.roles || [], parsedAt: new Date().toISOString() };`,
        },
        x: 320,
        y: 160,
      },
      {
        id: 'lj-filter',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Keep only relevant roles',
          transform: `const minScore = 0.6;
const profile = input.profile || { titles: ['Manager', 'Lead', 'Director'] };
const scored = (input.roles || []).map((r) => {
  const match = profile.titles.some((t) => (r.title || '').includes(t));
  return { ...r, relevance: match ? 0.8 : 0.3 };
});
return {
  relevantRoles: scored.filter((r) => r.relevance >= minScore),
  skipped: scored.length - scored.filter((r) => r.relevance >= minScore).length,
};`,
        },
        x: 560,
        y: 160,
      },
      {
        id: 'lj-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Send digest: relevant roles only',
          type: 'info',
          message: 'Your filtered LinkedIn job list is ready to review.',
        },
        x: 800,
        y: 160,
      },
      {
        id: 'lj-store',
        type: 'action',
        subtype: 'storage',
        config: { name: 'Save for dashboard', key: 'linkedin_relevant_jobs' },
        x: 1040,
        y: 160,
      },
    ],
    edges: [
      { id: 'lj-e1', from: 'lj-trigger', to: 'lj-parse' },
      { id: 'lj-e2', from: 'lj-parse', to: 'lj-filter' },
      { id: 'lj-e3', from: 'lj-filter', to: 'lj-notify' },
      { id: 'lj-e4', from: 'lj-notify', to: 'lj-store' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },

  job_application_tracker: {
    id: 'template-007',
    name: '📋 Job application tracker',
    difficulty: 'Beginner',
    category: 'Career & Jobs',
    useCase: 'Log every application — company, role, date, and status — in your personal dashboard.',
    description: 'Manual or webhook trigger logs each application for your tracker dashboard.',
    features: ['Manual trigger', 'Application logging', 'Status tracking', 'Dashboard storage'],
    nodes: [
      {
        id: 'jt-trigger',
        type: 'trigger',
        subtype: 'manual',
        config: { name: 'Log new application' },
        x: 80,
        y: 160,
      },
      {
        id: 'jt-format',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Format application record',
          transform: `return {
  id: 'app-' + Date.now(),
  company: input.company || 'Unknown',
  role: input.role || 'Unknown',
  appliedAt: new Date().toISOString(),
  channel: input.channel || 'linkedin',
  status: input.status || 'applied',
  followUpDue: input.followUpDue || null,
  notes: input.notes || '',
};`,
        },
        x: 320,
        y: 160,
      },
      {
        id: 'jt-store',
        type: 'action',
        subtype: 'storage',
        config: { name: 'Add to tracker dashboard', key: 'job_application_tracker' },
        x: 560,
        y: 160,
      },
      {
        id: 'jt-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Confirm logged',
          type: 'success',
          message: 'Application saved to your tracker.',
        },
        x: 800,
        y: 160,
      },
    ],
    edges: [
      { id: 'jt-e1', from: 'jt-trigger', to: 'jt-format' },
      { id: 'jt-e2', from: 'jt-format', to: 'jt-store' },
      { id: 'jt-e3', from: 'jt-store', to: 'jt-notify' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },

  job_followup_reminder: {
    id: 'template-008',
    name: '⏰ Job follow-up reminder',
    difficulty: 'Beginner',
    category: 'Career & Jobs',
    useCase: 'Schedule a reminder 5 days after applying to follow up with the recruiter.',
    description: 'After logging an application, schedule a follow-up reminder so you never forget to check in.',
    features: ['Manual trigger', 'Follow-up scheduling', 'Reminder notification', 'Tracker update'],
    nodes: [
      {
        id: 'jf-trigger',
        type: 'trigger',
        subtype: 'manual',
        config: { name: 'After application logged' },
        x: 80,
        y: 160,
      },
      {
        id: 'jf-schedule',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Schedule follow-up in 5 days',
          transform: `const d = new Date();
d.setDate(d.getDate() + (input.daysUntilFollowUp || 5));
return {
  ...input,
  followUpAt: d.toISOString(),
  reminderTitle: 'Follow up: ' + (input.company || 'application'),
};`,
        },
        x: 320,
        y: 160,
      },
      {
        id: 'jf-store',
        type: 'action',
        subtype: 'storage',
        config: { name: 'Save reminder', key: 'job_followup_reminders' },
        x: 560,
        y: 160,
      },
      {
        id: 'jf-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Follow-up scheduled',
          type: 'info',
          message: 'You will be reminded to follow up on this application.',
        },
        x: 800,
        y: 160,
      },
    ],
    edges: [
      { id: 'jf-e1', from: 'jf-trigger', to: 'jf-schedule' },
      { id: 'jf-e2', from: 'jf-schedule', to: 'jf-store' },
      { id: 'jf-e3', from: 'jf-store', to: 'jf-notify' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },

  linkedin_hiring_manager_intro: {
    id: 'template-009',
    name: '💬 LinkedIn intro to hiring manager',
    difficulty: 'Beginner',
    category: 'Career & Jobs',
    useCase:
      'Draft a polite LinkedIn message saying you applied and would love to learn more about the role.',
    description:
      'Personalized LinkedIn outreach to the hiring manager or recruiter named in the job posting.',
    features: ['Manual trigger', 'Message drafting', 'Hiring manager field', 'Review before send'],
    nodes: [
      {
        id: 'lm-trigger',
        type: 'trigger',
        subtype: 'manual',
        config: { name: 'Prepare outreach' },
        x: 80,
        y: 160,
      },
      {
        id: 'lm-draft',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Draft intro message',
          transform: `const m = input.hiringManager || { firstName: 'there' };
return {
  linkedInMessage:
    'Hi ' + m.firstName + ', I recently applied for the ' + input.roleTitle +
    ' position at ' + input.company + ". I'm very interested and would " +
    'appreciate any insight about the team or hiring timeline. Thank you!",
  reviewRequired: true,
  profileUrl: m.profileUrl || '',
};`,
        },
        x: 320,
        y: 160,
      },
      {
        id: 'lm-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Ready for your review',
          type: 'info',
          message: 'LinkedIn message drafted — approve before sending.',
        },
        x: 560,
        y: 160,
      },
      {
        id: 'lm-log',
        type: 'action',
        subtype: 'log',
        config: {
          name: 'Log outreach attempt',
          message: 'Outreach draft created for hiring manager.',
        },
        x: 800,
        y: 160,
      },
    ],
    edges: [
      { id: 'lm-e1', from: 'lm-trigger', to: 'lm-draft' },
      { id: 'lm-e2', from: 'lm-draft', to: 'lm-notify' },
      { id: 'lm-e3', from: 'lm-notify', to: 'lm-log' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },

  receipt_email_to_tracker: {
    id: 'template-010',
    name: '🧾 Receipt email → expense tracker',
    difficulty: 'Beginner',
    category: 'Personal Finance',
    useCase: 'When a store emails a receipt, log the amount and merchant to your spending tracker.',
    description: 'Parse purchase receipt emails and log amount, merchant, and date.',
    features: ['Email webhook', 'Receipt parsing', 'Amount extraction', 'Expense log'],
    nodes: [
      {
        id: 'rc-trigger',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'Receipt email received',
          url: 'https://your-automation.com/webhook/email/receipt',
        },
        x: 80,
        y: 160,
      },
      {
        id: 'rc-parse',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Extract merchant & total',
          transform: `return {
  merchant: input.merchant || 'Unknown store',
  amount: input.total || 0,
  currency: input.currency || 'USD',
  purchasedAt: input.date || new Date().toISOString(),
  category: input.category || 'uncategorized',
};`,
        },
        x: 320,
        y: 160,
      },
      {
        id: 'rc-store',
        type: 'action',
        subtype: 'storage',
        config: { name: 'Add to expense tracker', key: 'personal_expenses' },
        x: 560,
        y: 160,
      },
      {
        id: 'rc-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Expense logged',
          type: 'success',
          message: 'Receipt added to your spending tracker.',
        },
        x: 800,
        y: 160,
      },
    ],
    edges: [
      { id: 'rc-e1', from: 'rc-trigger', to: 'rc-parse' },
      { id: 'rc-e2', from: 'rc-parse', to: 'rc-store' },
      { id: 'rc-e3', from: 'rc-store', to: 'rc-notify' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },

  appointment_reminder: {
    id: 'template-011',
    name: '📅 Appointment reminder (24h before)',
    difficulty: 'Beginner',
    category: 'Personal Productivity',
    useCase: 'Get a reminder the day before a dentist, doctor, or meeting you booked online.',
    description: 'Booking webhook triggers a reminder notification 24 hours before the appointment.',
    features: ['Webhook trigger', 'Date parsing', 'Reminder scheduling', 'Notification'],
    nodes: [
      {
        id: 'ap-trigger',
        type: 'trigger',
        subtype: 'webhook',
        config: {
          name: 'New appointment booked',
          url: 'https://your-automation.com/webhook/calendar/booking',
        },
        x: 80,
        y: 160,
      },
      {
        id: 'ap-parse',
        type: 'tool',
        subtype: 'transform',
        config: {
          name: 'Calculate reminder time',
          transform: `const start = new Date(input.startTime);
const remind = new Date(start.getTime() - 24 * 60 * 60 * 1000);
return {
  title: input.title || 'Appointment',
  location: input.location || '',
  startsAt: start.toISOString(),
  remindAt: remind.toISOString(),
};`,
        },
        x: 320,
        y: 160,
      },
      {
        id: 'ap-store',
        type: 'action',
        subtype: 'storage',
        config: { name: 'Queue reminder', key: 'appointment_reminders' },
        x: 560,
        y: 160,
      },
      {
        id: 'ap-notify',
        type: 'action',
        subtype: 'notification',
        config: {
          name: 'Reminder scheduled',
          type: 'info',
          message: 'You will be reminded 24 hours before your appointment.',
        },
        x: 800,
        y: 160,
      },
    ],
    edges: [
      { id: 'ap-e1', from: 'ap-trigger', to: 'ap-parse' },
      { id: 'ap-e2', from: 'ap-parse', to: 'ap-store' },
      { id: 'ap-e3', from: 'ap-store', to: 'ap-notify' },
    ],
    createdAt: now(),
    updatedAt: now(),
  },
};

export type { UserTemplate };
